import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner@2.0.3";
import { Plus, Trash, Eye, Pencil, Save, Upload, X } from "lucide-react";
import {
  createProject,
  getAllProjects,
  deleteProject,
  updateProject,
  getAdminProjectById,
  ProjectPayload,
  mediaService,
} from "../../services/Project";
import { API_ORIGIN } from "../../services/apiConfig";
import { withCurrent } from "../../services/SiteSettings";
import { isRichTextEmpty } from "../../services/richText";
import { RichTextEditor } from "./RichTextEditor";
import {
  useAdminTaxonomy,
  TaxonomyManagerDialog,
  ManageOptionsButton,
  TaxonomySection,
} from "./TaxonomyManager";

export function AddProjectForm() {
  const { taxonomy, saveTaxonomy } = useAdminTaxonomy();
  const [managing, setManaging] = useState<TaxonomySection | null>(null);
  const THEMATIC_AREAS = taxonomy.thematicAreas.map((a) => a.name);
  const PROJECT_ASSIGNMENT_TYPES = taxonomy.projectAssignmentTypes;
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "",
    typeOfAssignment: "",
    status: "",
    year: "",
    completedYear: "", // Added completedYear field
    location: "",
    imageUrl: "",
    tags: [""],
  });

  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<any | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Image preview state for main form
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

  // Image preview state for edit dialog
  const [editImagePreview, setEditImagePreview] = useState<string | null>(null);
  const [editSelectedImageFile, setEditSelectedImageFile] = useState<File | null>(null);
  const [editUploadedFileName, setEditUploadedFileName] = useState<string | null>(null);
  const [editUploading, setEditUploading] = useState(false);
  const [editDeleting, setEditDeleting] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    thematicArea: 'all',
    typeOfAssignment: 'all',
    completedYear: 'all'
  });

  // Generate years from 2017 to 2035
  const generateYears = () => {
    const years = [];
    for (let year = 2017; year <= 2035; year++) {
      years.push(year);
    }
    return years;
  };

  const years = generateYears();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const data = await getAllProjects();
      setProjects(data);
    } catch (error) {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  // Filter functions
  const handleFilterChange = (filterType: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      thematicArea: 'all',
      typeOfAssignment: 'all',
      completedYear: 'all'
    });
  };

  const filteredProjects = projects.filter(project => {
    // Thematic Area filter
    if (filters.thematicArea !== 'all' && project.category !== filters.thematicArea) {
      return false;
    }

    // Type of Assignment filter
    if (filters.typeOfAssignment !== 'all' && project.typeOfAssignment !== filters.typeOfAssignment) {
      return false;
    }

    // Completed Year filter
    if (filters.completedYear !== 'all' && project.completedYear !== filters.completedYear) {
      return false;
    }

    return true;
  });

  const isFilterActive = filters.thematicArea !== 'all' ||
    filters.typeOfAssignment !== 'all' ||
    filters.completedYear !== 'all';

  const handleImageUpload = async (file: File) => {
    setUploading(true);
    try {
      const response = await mediaService.uploadMedia(
        [{ file }],
        { name: 'projects' }
      );

      if (response.uploadedAssets && response.uploadedAssets.length > 0) {
        const uploadedImage = response.uploadedAssets[0];
        const imageUrl = uploadedImage.src || `${API_ORIGIN}/usp/assets/view/${uploadedImage.fileName}`;

        setFormData(prev => ({ ...prev, imageUrl: imageUrl }));
        setUploadedFileName(uploadedImage.fileName);
        toast.success("Image uploaded successfully!");
        return { imageUrl, fileName: uploadedImage.fileName };
      } else {
        throw new Error("No uploaded assets returned");
      }
    } catch (error: any) {
      toast.error(`Image upload failed: ${error.message}`);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleEditImageUpload = async (file: File) => {
    setEditUploading(true);
    try {
      const response = await mediaService.uploadMedia(
        [{ file }],
        { name: 'projects' }
      );

      if (response.uploadedAssets && response.uploadedAssets.length > 0) {
        const uploadedImage = response.uploadedAssets[0];
        const imageUrl = uploadedImage.src || `${API_ORIGIN}/usp/assets/view/${uploadedImage.fileName}`;

        setSelectedProject((prev: any) => ({ ...prev, imageUrl: imageUrl }));
        setEditUploadedFileName(uploadedImage.fileName);
        toast.success("Image uploaded successfully!");
        return { imageUrl, fileName: uploadedImage.fileName };
      } else {
        throw new Error("No uploaded assets returned");
      }
    } catch (error: any) {
      toast.error(`Image upload failed: ${error.message}`);
      throw error;
    } finally {
      setEditUploading(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!uploadedFileName) {
      removeImage();
      return;
    }

    setDeleting(true);
    try {
      await mediaService.deleteMedia(uploadedFileName);
      toast.success("Image deleted successfully!");
      removeImage();
    } catch (error: any) {
      toast.error(`Image deletion failed: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleEditDeleteImage = async () => {
    if (!editUploadedFileName) {
      removeEditImage();
      return;
    }

    setEditDeleting(true);
    try {
      await mediaService.deleteMedia(editUploadedFileName);
      toast.success("Image deleted successfully!");
      removeEditImage();
    } catch (error: any) {
      toast.error(`Image deletion failed: ${error.message}`);
    } finally {
      setEditDeleting(false);
    }
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, imageUrl: "" }));
    setImagePreview(null);
    setSelectedImageFile(null);
    setUploadedFileName(null);

    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
  };

  const removeEditImage = () => {
    setSelectedProject((prev: any) => ({ ...prev, imageUrl: "" }));
    setEditImagePreview(null);
    setEditSelectedImageFile(null);
    setEditUploadedFileName(null);

    if (editImagePreview) {
      URL.revokeObjectURL(editImagePreview);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error("Please select an image file");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }

      setSelectedImageFile(file);

      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);

      handleImageUpload(file).catch(() => {
        setImagePreview(null);
        setSelectedImageFile(null);
      });
    }
  };

  const handleEditFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error("Please select an image file");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }

      setEditSelectedImageFile(file);

      const previewUrl = URL.createObjectURL(file);
      setEditImagePreview(previewUrl);

      handleEditImageUpload(file).catch(() => {
        setEditImagePreview(null);
        setEditSelectedImageFile(null);
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isRichTextEmpty(formData.description)) {
      toast.error("Please enter a project description");
      return;
    }

    try {
      const payload: ProjectPayload = {
        ...formData,
        tags: formData.tags.filter((t) => t.trim() !== ""),
      };
      await createProject(payload);
      toast.success("Project added successfully!");
      setFormData({
        title: "",
        description: "",
        category: "",
        typeOfAssignment: "",
        status: "",
        year: "",
        completedYear: "", // Reset completedYear
        location: "",
        imageUrl: "",
        tags: [""],
      });
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
        setImagePreview(null);
      }
      setSelectedImageFile(null);
      setUploadedFileName(null);
      fetchProjects();
    } catch (error: any) {
      toast.error(`${error.message || "Failed to add project"}`);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this project?")) return;
    try {
      await deleteProject(id);
      toast.success("🗑 Project deleted successfully!");
      fetchProjects();
    } catch (error) {
      toast.error("Failed to delete project");
    }
  };

  const handleView = async (id: string) => {
    try {
      const data = await getAdminProjectById(id);
      const project = data?.project || data;

      setSelectedProject({
        ...project,
        tags: Array.isArray(project.tags)
          ? project.tags
          : project.tags
            ? [project.tags]
            : [""],
      });
      setIsEditing(false);
      setViewDialogOpen(true);

      // Reset edit image states
      setEditImagePreview(null);
      setEditSelectedImageFile(null);
      setEditUploadedFileName(null);
    } catch (error: any) {
      toast.error(error.message || "Failed to fetch project details");
    }
  };

  const handleUpdate = async () => {
    if (!selectedProject) return;
    if (isRichTextEmpty(selectedProject.description)) {
      toast.error("Please enter a project description");
      return;
    }
    try {
      const payload: ProjectPayload = {
        ...selectedProject,
        tags: Array.isArray(selectedProject.tags)
          ? selectedProject.tags.filter((t: string) => t.trim() !== "")
          : [],
      };
      await updateProject(selectedProject.id, payload);
      toast.success("Project updated successfully!");
      setIsEditing(false);
      fetchProjects();
      setViewDialogOpen(false);
    } catch (error: any) {
      toast.error(error.message || "Failed to update project");
    }
  };

  const handleProjectFieldChange = (field: string, value: string) => {
    setSelectedProject((prev: any) => ({ ...prev, [field]: value }));
  };

  // Tags edit handlers
  const handleTagChangeEdit = (index: number, value: string) => {
    setSelectedProject((prev: any) => {
      const arr = Array.isArray(prev.tags) ? [...prev.tags] : [""];
      arr[index] = value;
      return { ...prev, tags: arr };
    });
  };

  const addTagEdit = () => {
    setSelectedProject((prev: any) => ({
      ...prev,
      tags: Array.isArray(prev.tags) ? [...prev.tags, ""] : [""],
    }));
  };

  const removeTagEdit = (index: number) => {
    setSelectedProject((prev: any) => ({
      ...prev,
      tags: Array.isArray(prev.tags)
        ? prev.tags.filter((_: any, i: number) => i !== index)
        : [],
    }));
  };

  // Form field handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSelectChange = (name: string, value: string) =>
    setFormData({ ...formData, [name]: value });

  const handleTagChange = (index: number, value: string) => {
    const newTags = [...formData.tags];
    newTags[index] = value;
    setFormData({ ...formData, tags: newTags });
  };

  const addTag = () => setFormData({ ...formData, tags: [...formData.tags, ""] });
  const removeTag = (index: number) =>
    setFormData({ ...formData, tags: formData.tags.filter((_, i) => i !== index) });

  return (
    <>
      <TaxonomyManagerDialog
        section={managing}
        taxonomy={taxonomy}
        onClose={() => setManaging(null)}
        onSave={saveTaxonomy}
      />

      {/* Add Project Form */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Add New Project</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title ,Thematic Area, Types of Assignment */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Project Title *</Label>
                <Input
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter project title"
                  required
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Thematic Area *</Label>
                  <ManageOptionsButton label="Add or edit thematic areas" onClick={() => setManaging("thematicAreas")} />
                </div>
                <Select
                  value={formData.category}
                  onValueChange={(v) => handleSelectChange("category", v)}
                >
                  <SelectTrigger className="cursor-pointer">
                    <SelectValue placeholder="Select Thematic Area" />
                  </SelectTrigger>
                  <SelectContent>
                    {withCurrent(THEMATIC_AREAS, formData.category).map((area) => (
                      <SelectItem key={area} value={area} className="cursor-pointer">{area}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Types of Assignment *</Label>
                  <ManageOptionsButton label="Add or edit types of assignment" onClick={() => setManaging("projectAssignmentTypes")} />
                </div>
                <Select
                  value={formData.typeOfAssignment}
                  onValueChange={(v) => handleSelectChange("typeOfAssignment", v)}
                >
                  <SelectTrigger className="cursor-pointer">
                    <SelectValue placeholder="Select Types of Assignment" />
                  </SelectTrigger>
                  <SelectContent>
                    {withCurrent(PROJECT_ASSIGNMENT_TYPES, formData.typeOfAssignment).map((type) => (
                      <SelectItem key={type} value={type} className="cursor-pointer">{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Status, Year, Location */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Status *</Label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => handleSelectChange("status", v)}
                >
                  <SelectTrigger className="cursor-pointer">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE" className="cursor-pointer">Active</SelectItem>
                    <SelectItem value="INACTIVE" className="cursor-pointer">InActive</SelectItem>
                    <SelectItem value="COMPLETED" className="cursor-pointer">Completed</SelectItem>
                    <SelectItem value="ON_HOLD" className="cursor-pointer">OnHold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Project Period *</Label>
                <Input
                  name="year"
                  value={formData.year}
                  onChange={handleInputChange}
                  placeholder="2024"
                  required
                />
              </div>
              <div>
                <Label>Client *</Label>
                <Input
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="Kathmandu, Nepal"
                  required
                />
              </div>
            </div>

            {/* Completed Year */}
            <div>
              <Label>Completed Year *</Label>
              <Select
                value={formData.completedYear}
                onValueChange={(v) => handleSelectChange("completedYear", v)}
              >
                <SelectTrigger className="cursor-pointer">
                  <SelectValue placeholder="Select Completed Year" />
                </SelectTrigger>
                <SelectContent>
                  {years.map((year) => (
                    <SelectItem key={year} value={year.toString()} className="cursor-pointer">
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div>
              <Label>Description *</Label>
              <RichTextEditor
                value={formData.description}
                onChange={(html) => setFormData((prev) => ({ ...prev, description: html }))}
                placeholder="Enter detailed project description..."
              />
            </div>

            {/* Image Upload Section */}
            <div>
              <Label>Project Image</Label>
              <div className="mt-2">
                {!formData.imageUrl ? (
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      disabled={uploading}
                      className="hidden"
                      id="image-upload"
                    />
                    <label
                      htmlFor="image-upload"
                      className={`block border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors cursor-pointer ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className="flex flex-col items-center justify-center gap-4">
                        <div className="p-3 bg-gray-100 rounded-full">
                          <Upload className="w-6 h-6 text-gray-500" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-1">
                            Click anywhere to upload project image (Optional)
                          </p>
                          <p className="text-xs text-gray-500">
                            PNG, JPG, JPEG files. Max 5MB.
                          </p>
                        </div>
                        <div className={`inline-flex items-center px-6 py-3 rounded-md text-sm font-medium transition-colors ${uploading
                          ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                          : "bg-black text-white hover:bg-gray-800 cursor-pointer"
                          }`}>
                          <Upload className="w-4 h-4 mr-2" />
                          {uploading ? "Uploading..." : "Select Image"}
                        </div>
                      </div>
                    </label>
                  </div>
                ) : (
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col sm:flex-row items-start gap-4">
                      <div className="relative flex-shrink-0">
                        <div className="w-full sm:w-64 h-48 border-2 border-gray-200 rounded-lg overflow-hidden">
                          <img
                            src={imagePreview || formData.imageUrl}
                            alt="Project preview"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.src = '/placeholder-image.jpg';
                            }}
                          />
                        </div>
                      </div>

                      <div className="flex flex-col gap-3 flex-1">
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={handleDeleteImage}
                          disabled={deleting}
                          className="w-fit cursor-pointer"
                        >
                          <Trash className="w-4 h-4 mr-2" />
                          {deleting ? "Deleting..." : "Delete Image"}
                        </Button>

                        <div className="flex items-center gap-2 text-sm">
                          {uploading && (
                            <div className="flex items-center gap-2 text-blue-600">
                              <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                              Uploading image...
                            </div>
                          )}
                          {deleting && (
                            <div className="flex items-center gap-2 text-orange-600">
                              <div className="w-2 h-2 bg-orange-600 rounded-full animate-pulse"></div>
                              Deleting image...
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Tags */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Project Tags</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addTag}
                  className="cursor-pointer"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
              {formData.tags.map((t, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <Input
                    value={t}
                    onChange={(e) => handleTagChange(i, e.target.value)}
                    placeholder={`Tag ${i + 1}`}
                  />
                  {formData.tags.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeTag(i)}
                      className="cursor-pointer"
                    >
                      <Trash className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <Button
              type="submit"
              className="w-full cursor-pointer"
              disabled={uploading || deleting}
            >
              {uploading ? "Uploading Image..." : deleting ? "Deleting Image..." : "Add Project"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Projects Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Projects</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filters Section */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
            {/* Thematic Area Filter */}
            <div>
              <Label htmlFor="thematicFilter" className="text-sm font-medium">Thematic Area</Label>
              <Select
                value={filters.thematicArea}
                onValueChange={(value) => handleFilterChange('thematicArea', value)}
              >
                <SelectTrigger id="thematicFilter" className="cursor-pointer">
                  <SelectValue placeholder="All Thematic Areas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="cursor-pointer">All Thematic Areas</SelectItem>
                  {THEMATIC_AREAS.map((area) => (
                    <SelectItem key={area} value={area} className="cursor-pointer">{area}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Type of Assignment Filter */}
            <div>
              <Label htmlFor="assignmentFilter" className="text-sm font-medium">Type of Assignment</Label>
              <Select
                value={filters.typeOfAssignment}
                onValueChange={(value) => handleFilterChange('typeOfAssignment', value)}
              >
                <SelectTrigger id="assignmentFilter" className="cursor-pointer">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="cursor-pointer">All Types</SelectItem>
                  {PROJECT_ASSIGNMENT_TYPES.map((type) => (
                    <SelectItem key={type} value={type} className="cursor-pointer">{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Completed Year Filter */}
            <div>
              <Label htmlFor="yearFilter" className="text-sm font-medium">Completed Year</Label>
              <Select
                value={filters.completedYear}
                onValueChange={(value) => handleFilterChange('completedYear', value)}
              >
                <SelectTrigger id="yearFilter" className="cursor-pointer">
                  <SelectValue placeholder="All Years" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="cursor-pointer">All Years</SelectItem>
                  {years.map((year) => (
                    <SelectItem key={year} value={year.toString()} className="cursor-pointer">
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Clear Filters Button */}
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={clearFilters}
                disabled={!isFilterActive}
                className="cursor-pointer w-full"
              >
                <X className="w-4 h-4 mr-2" />
                Clear Filters
              </Button>
            </div>
          </div>

          {/* Results Count */}
          {isFilterActive && (
            <div className="mb-4 px-2">
              <p className="text-sm text-gray-600">
                Showing {filteredProjects.length} of {projects.length} projects
                {filters.thematicArea !== 'all' && ` • Thematic Area: ${filters.thematicArea}`}
                {filters.typeOfAssignment !== 'all' && ` • Type: ${filters.typeOfAssignment}`}
                {filters.completedYear !== 'all' && ` • Year: ${filters.completedYear}`}
              </p>
            </div>
          )}

          <div className="overflow-x-auto mt-6">
            <table className="w-full border border-gray-200 text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2 text-left">#</th>
                  <th className="p-2 text-left">Project Title</th>
                  <th className="p-2 text-left">Thematic Area</th>
                  <th className="p-2 text-left">Type of Assignment</th>
                  <th className="p-2 text-left">Project Period</th>
                  <th className="p-2 text-left">Completed Year</th>
                  <th className="p-2 text-left">Client</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="p-4 text-center">Loading...</td>
                  </tr>
                ) : filteredProjects.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="p-4 text-center">
                      {projects.length === 0 ? 'No projects found' : 'No projects match the selected filters'}
                    </td>
                  </tr>
                ) : (
                  filteredProjects.map((project, index) => (
                    <tr key={project.id} className="border-t hover:bg-gray-50">
                      <td className="p-2">{index + 1}</td>
                      <td className="p-2">{project.title}</td>
                      <td className="p-2">{project.category}</td>
                      <td className="p-2">{project.typeOfAssignment || "-"}</td>
                      <td className="p-2">{project.year}</td>
                      <td className="p-2">{project.completedYear || "-"}</td>
                      <td className="p-2">{project.location}</td>
                      <td className="p-2">{project.status}</td>
                      <td className="p-2 flex justify-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleView(project.id)} className="cursor-pointer">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="destructive" size="sm" onClick={() => handleDelete(project.id)} className="cursor-pointer">
                          <Trash className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* View/Edit Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="w-full sm:max-w-2xl max-h-[90vh] flex flex-col h-full">
          <DialogHeader>
            <DialogTitle>{isEditing ? "Edit Project" : "Project Details"}</DialogTitle>
          </DialogHeader>

          {selectedProject && (
            <div className="flex-1 overflow-y-auto space-y-4 mt-4 pr-1">
              {/* Title + Category */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Project Title</Label>
                  <Input
                    value={selectedProject.title}
                    onChange={(e) => handleProjectFieldChange("title", e.target.value)}
                    disabled={!isEditing}
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Thematic Area</Label>
                    {isEditing && (
                      <ManageOptionsButton label="Add or edit thematic areas" onClick={() => setManaging("thematicAreas")} />
                    )}
                  </div>
                  {isEditing ? (
                    <Select
                      value={selectedProject.category}
                      onValueChange={(v) => handleProjectFieldChange("category", v)}
                    >
                      <SelectTrigger className="cursor-pointer">
                        <SelectValue placeholder="Select Thematic Area" />
                      </SelectTrigger>
                      <SelectContent>
                        {withCurrent(THEMATIC_AREAS, selectedProject.category).map((area) => (
                          <SelectItem key={area} value={area} className="cursor-pointer">{area}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input value={selectedProject.category} disabled />
                  )}
                </div>
              </div>

              {/* Type of Assignment + Status */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Type of Assignment</Label>
                    {isEditing && (
                      <ManageOptionsButton label="Add or edit types of assignment" onClick={() => setManaging("projectAssignmentTypes")} />
                    )}
                  </div>
                  {isEditing ? (
                    <Select
                      value={selectedProject.typeOfAssignment}
                      onValueChange={(v) => handleProjectFieldChange("typeOfAssignment", v)}
                    >
                      <SelectTrigger className="cursor-pointer">
                        <SelectValue placeholder="Select Type of Assignment" />
                      </SelectTrigger>
                      <SelectContent>
                        {withCurrent(PROJECT_ASSIGNMENT_TYPES, selectedProject.typeOfAssignment).map((type) => (
                          <SelectItem key={type} value={type} className="cursor-pointer">{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input value={selectedProject.typeOfAssignment || "-"} disabled />
                  )}
                </div>
                <div>
                  <Label>Status</Label>
                  {isEditing ? (
                    <Select
                      value={selectedProject.status}
                      onValueChange={(v) => handleProjectFieldChange("status", v)}
                    >
                      <SelectTrigger className="cursor-pointer">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ACTIVE" className="cursor-pointer">Active</SelectItem>
                        <SelectItem value="INACTIVE" className="cursor-pointer">InActive</SelectItem>
                        <SelectItem value="COMPLETED" className="cursor-pointer">Completed</SelectItem>
                        <SelectItem value="ON_HOLD" className="cursor-pointer">OnHold</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input value={selectedProject.status} disabled />
                  )}
                </div>
              </div>

              {/* Year + Location */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Project Period</Label>
                  <Input
                    value={selectedProject.year}
                    onChange={(e) => handleProjectFieldChange("year", e.target.value)}
                    disabled={!isEditing}
                  />
                </div>
                <div>
                  <Label>Client</Label>
                  <Input
                    value={selectedProject.location}
                    onChange={(e) => handleProjectFieldChange("location", e.target.value)}
                    disabled={!isEditing}
                  />
                </div>
              </div>

              {/* Completed Year */}
              <div>
                <Label>Completed Year</Label>
                {isEditing ? (
                  <Select
                    value={selectedProject.completedYear}
                    onValueChange={(v) => handleProjectFieldChange("completedYear", v)}
                  >
                    <SelectTrigger className="cursor-pointer">
                      <SelectValue placeholder="Select Completed Year" />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((year) => (
                        <SelectItem key={year} value={year.toString()} className="cursor-pointer">
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={selectedProject.completedYear || "-"} disabled />
                )}
              </div>

              {/* Description */}
              <div>
                <Label>Description</Label>
                <RichTextEditor
                  value={selectedProject.description || ""}
                  onChange={(html) => handleProjectFieldChange("description", html)}
                  disabled={!isEditing}
                  minHeight={140}
                />
              </div>

              {/* Image Upload Section for Edit Dialog */}
              <div>
                <Label>Project Image</Label>
                <div className="mt-2">
                  {!selectedProject.imageUrl ? (
                    isEditing ? (
                      <div className="relative">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleEditFileChange}
                          disabled={editUploading}
                          className="hidden"
                          id="edit-image-upload"
                        />
                        <label
                          htmlFor="edit-image-upload"
                          className={`block border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors cursor-pointer ${editUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <div className="flex flex-col items-center justify-center gap-3">
                            <div className="p-2 bg-gray-100 rounded-full">
                              <Upload className="w-5 h-5 text-gray-500" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-700 mb-1">
                                Click to upload project image (Optional)
                              </p>
                              <p className="text-xs text-gray-500">
                                PNG, JPG, JPEG files. Max 5MB.
                              </p>
                            </div>
                            <div className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${editUploading
                              ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                              : "bg-black text-white hover:bg-gray-800 cursor-pointer"
                              }`}>
                              <Upload className="w-4 h-4 mr-2" />
                              {editUploading ? "Uploading..." : "Select Image"}
                            </div>
                          </div>
                        </label>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500 italic">No image uploaded</div>
                    )
                  ) : (
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-col sm:flex-row items-start gap-4">
                        <div className="relative flex-shrink-0">
                          <div className="w-full sm:w-56 h-40 border-2 border-gray-200 rounded-lg overflow-hidden">
                            <img
                              src={editImagePreview || selectedProject.imageUrl}
                              alt="Project preview"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.src = '/placeholder-image.jpg';
                              }}
                            />
                          </div>
                        </div>

                        <div className="flex flex-col gap-3 flex-1">
                          {isEditing && (
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              onClick={handleEditDeleteImage}
                              disabled={editDeleting}
                              className="w-fit cursor-pointer"
                            >
                              <Trash className="w-4 h-4 mr-2" />
                              {editDeleting ? "Deleting..." : "Delete Image"}
                            </Button>
                          )}

                          <div className="flex items-center gap-2 text-sm">
                            {editUploading && (
                              <div className="flex items-center gap-2 text-blue-600">
                                <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                                Uploading image...
                              </div>
                            )}
                            {editDeleting && (
                              <div className="flex items-center gap-2 text-orange-600">
                                <div className="w-2 h-2 bg-orange-600 rounded-full animate-pulse"></div>
                                Deleting image...
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Tags */}
              <div>
                <Label>Tags</Label>
                {Array.isArray(selectedProject.tags) &&
                  selectedProject.tags.map((t: string, i: number) => (
                    <div key={i} className="flex gap-2 mb-2">
                      <Input
                        value={t}
                        onChange={(e) => handleTagChangeEdit(i, e.target.value)}
                        disabled={!isEditing}
                      />
                      {isEditing && (
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => removeTagEdit(i)}
                          className="cursor-pointer"
                        >
                          <Trash className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                {isEditing && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addTagEdit}
                    className="cursor-pointer"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-2 mt-4">
            {!isEditing ? (
              <Button onClick={() => setIsEditing(true)} className="cursor-pointer">
                <Pencil className="w-4 h-4 mr-1" /> Edit
              </Button>
            ) : (
              <Button
                onClick={handleUpdate}
                className="cursor-pointer"
                disabled={editUploading || editDeleting}
              >
                <Save className="w-4 h-4 mr-1" />
                {editUploading ? "Uploading..." : editDeleting ? "Deleting..." : "Save"}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}